/** @jsx jsx */

import { Fragment as _, useState, useContext, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import { unlockInteraction } from 'services/unlockInteraction';
import isIE from 'services/isIE';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import getStore from 'services/getStore';
import EditWrapper from './EditWrapper';
import { useSelector } from 'react-redux';
import hotspotDefaultSVG from 'assets/HotspotDefault_v3.svg';
import hexToRGB from 'services/hexToRGB';
import getBlockById from 'services/apiHelpers/getBlockById';
import Feedback from './Feedback';
import VisibleHotSpot from './VisibleHotSpot';
import { Polygon } from './Polygon';
import Rectangle from './Rectangle';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import pubSub from 'services/pubSub';
import setNewBlockNewTree from 'services/apiHelpers/setNewBlockNewTree';
import { getNextHotSpotBlock } from 'factories/interactions/hotspot_isolated';
import { setBlocksAction } from 'actions/setBlocksAction';

const store = getStore();

const hasBeenSeen: { [key: string]: boolean } = {};

const HotSpot = ({ children, ...props }) => {
  let {
    id,
    image,
    src,
    hotspotType = 'circle',
    hotSpotColor,
    hotSpotSize,
    hotSpots,
    hotSpotsAnimated = true,
    hotSpotsVisible = true,
    useMediaInFeedback = true,
    showSeenState = true,
  } = props;

  if (src) {
    image = src;
  }

  const context: any = useContext(interactionContext);

  const selected = getStore().getState().focusedNodeId === id;

  const [imgSize, setImgSize] = useState([960, 465]);
  const [imgLoading, setImgLoading] = useState(0);
  const [creating, setCreating] = useState(false);
  const [showFeedback, setShowFeedback] = useState<string | null>(null);
  const [{ scale, inverseScale }, setScale] = useState({
    scale: 1 || 0.3,
    inverseScale: 1 || 1 * (1 / 0.3),
  });

  const [currentHotspotId, setCurrentHotspotId] = useState<string | undefined>(
    undefined
  );

  const container = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const currentHotspot = useRef<string | null>(null);
  const hasPointsOnProps = useRef<boolean | undefined>(undefined);

  const drawing = useRef<boolean>(false);
  const startXY = useRef<{ startX: number; startY: number }>({
    startX: 0,
    startY: 0,
  });
  const rectState = useRef<any>({});

  let { isOpen } = context;

  const interactionsOpen: any = useSelector(
    (state: any) => state.interactionsOpen
  );

  if (interactionsOpen.includes(context.id)) {
    isOpen = true;
  }

  const { r, g, b } = hexToRGB(hotSpotColor);

  const getParsedColor = opacity => {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  //

  const names: string[] = [];
  const points: (number[] | number)[] = children.map((child, index) => {
    const { name, points, updated } = child?.props;

    // console.log(name, updated, hotSpots);

    names.push(name);

    // if there's no hotpsots it's because they were removed explicitly by code
    // in the last update cycle
    // WE DO NOT WANT TO DO THAT GOING FORWARD !!!
    // we set an updated prop to some value explicitly when that point has been
    // updated in the editor. we can preference it then for use
    // otherwise. we use what's on the hotSpots

    //

    // can't use hotSpots[name] since there's potentially dupes
    return !hotSpots || updated ? points : Object.values(hotSpots)[index];
  });

  console.log({
    names,
    'names.join()': names?.join?.(),
    points,
    'points.join()': points?.join?.(),
  });

  const rectangleCoords = useRef<any>({});
  const itemsSeen = useRef(unlockInteraction(context.id, names.length));

  useEffect(() => {
    if (!hotSpots) {
      return;
    }

    // note :
    // the goal here is to update the points on props to match
    // what we get from the hotSpots.points
    //
    // if we get errors we can compare the values
    // go to the projects blocks directly and remove the ' zombie ' points on the props ( look for hotSpotName )
    // then when this runs again it should update
    // or. since we have the values off hotSpots.points we can update the props.points with those values

    const someHotspotsUpdated = children.some(child => {
      const { updated } = child.props; //hotSpot?.props;

      return updated;
    });

    // if any of the hotSpots have been updated we can return
    //
    if (someHotspotsUpdated) {
      return;
    }

    const [newBlock, newTree] = getNextHotSpotBlock(getBlockById(id))!;

    if (newBlock && newBlock.children) {
      const hotSpotPoints = Object.values(hotSpots);

      (newBlock.children as []).forEach((child: any, index) => {
        // somewhere in here check for no content maybe ?
        // no. just ensure there's no diff between names / points ( length ) and children.length ?
        const hotSpot = getBlockById(child.id) as any;

        const { name } = hotSpot?.props;

        if (name) {
          // can't use hotSpots[name] since there's potentially dupes
          // hotSpots[name] ? hotSpots[name] : [];

          hotSpot.props.points = hotSpotPoints[index] || [];
        }
      });
    }

    // we were removing the hotSpots prop in the old case. DO NOT DO THAT !!!!
    // we'll keep them to aid in debugging later if it comes to that

    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (window.dev()) return;

    if (context.props.lockDown === 0) return;
    showFeedback && itemsSeen.current(showFeedback);
  });

  useEffect(() => {
    const pS = pubSub.subscribe(`CURRENT_HOTSPOT_ID_${id}`, $ => {
      const { hotspotsBlockId, currentHotspotId } = $;

      setCurrentHotspotId(currentHotspotId);
    });

    return () => {
      pS();
    };
  });

  useEffect(() => {
    const pS = pubSub.subscribe(`ON_HOTSPOT_HOVER`, ({ hotspotId, hover }) => {
      const hotSpotRoot = document.querySelector(`#hotspots-${id}`);

      if (!hotSpotRoot) return;

      const markers = Array.from(
        hotSpotRoot.querySelectorAll(
          hotspotType === 'rectangle' ? 'rect' : hotspotType
        )
      ); //  ? 'circle' : 'polygon'

      if (!markers.length) {
        return;
      }

      markers.forEach(
        (marker: any) => (marker.style.fill = getParsedColor(0.5))
      ); // 'rgba(255, 0, 99, 0.5)'

      if (!hover) return;

      const marker: any = markers.find(
        (marker: any) => marker.id === `hotspot-${hotspotId}`
      );

      if (marker) {
        marker.style.fill = getParsedColor(0.8); // 'rgba(255, 0, 99, 0.8)'
      }
    });

    return () => {
      pS();
    };
  }, [hotspotType, hotSpotColor]);

  useEffect(() => {
    const onResize = () => {
      if (container.current) {
        const { width, height } = container.current.getBoundingClientRect();
        if (imgSize[0]) {
          let $ = width / imgSize[0];

          if (imgSize[0] <= width) {
            $ = 1;
          }

          mounted.current &&
            setScale({
              scale: $,
              inverseScale: 1 * (1 / $),
            });
        }
      }
    };

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, imgSize]);

  useEffect(() => {
    if (!image) {
      return;
    }

    //
    if (/\.(svg)$/i.test(image)) {
      fetch(image)
        .then(r => r.text())
        .then(t => {
          const svgContainer: any = document.createElement('div') as any;
          svgContainer.innerHTML = t;

          const viewBox = svgContainer
            .querySelector('svg')
            .getAttribute('viewBox')
            .split(' ');
          const [x, y, width, height] = viewBox;

          setImgLoading(2);
          setImgSize([width, height]);
        });
    } else {
      const img = new Image();
      img.src = image;
      setImgLoading(1);

      img.onload = () => {
        setImgLoading(2);
        setImgSize([img.width, img.height]);
      };
    }
  }, [isOpen, image]);

  let feedback = children.filter(child => {
    const { id, name } = child?.props as any;
    return [id, name].includes(showFeedback);
  });

  feedback = feedback[0]?.props.children;

  return !!imgSize.length ? (
    <div
      ref={container}
      css={css`
        position: relative;
        display: flex;
        justify-content: center;
        min-height: ${imgSize[1] * scale}px;

        ${isIE() &&
          `
          width: 100%;
          overflow: hidden;
    `}
      `}
    >
      <div
        css={css`
          position: relative;
          width: ${imgSize[0] * scale}px;
          height: ${imgSize[1] * scale}px;
          * {
            margin: 0;
            padding: 0;
          }
        `}
      >
        <div
          ref={root}
          id={`hotspots-${id}`}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: ${imgSize[0]}px;
            height: ${imgSize[1]}px;
            transform: scale(${scale});
            transform-origin: 0 0;
          `}
          onMouseDown={e => {
            if (hotspotType !== 'rectangle') {
              return;
            }

            drawing.current = true;
            startXY.current = {
              startX:
                (e.clientX - root.current!.getBoundingClientRect().left) *
                inverseScale,
              startY:
                (e.clientY - root.current!.getBoundingClientRect().top) *
                inverseScale,
            };

            rectState.current = {
              x: 0,
              y: 0,
              w: 0,
              h: 0,
            };

            pubSub.publish('UPDATE_HOTSPOT_POINTS', rectState.current);
          }}
          onMouseMove={e => {
            if (!drawing.current) {
              return;
            }

            const { startX, startY } = startXY.current;

            const clientX =
              (e.clientX - root.current!.getBoundingClientRect().left) *
              inverseScale;
            const clientY =
              (e.clientY - root.current!.getBoundingClientRect().top) *
              inverseScale;

            const negX = clientX < startX;
            const negY = clientY < startY;

            const x = negX ? Math.abs(clientX) : startX;
            const y = negY ? Math.abs(clientY) : startY;
            const w = Math.abs(clientX - startX);
            const h = Math.abs(clientY - startY);

            rectState.current = {
              x,
              y,
              w,
              h,
            };

            pubSub.publish('UPDATE_HOTSPOT_POINTS', {
              points: rectState.current,
              updateRectangleHotspotLocalState: true,
            });
          }}
          onMouseUp={e => {
            if (hotspotType !== 'rectangle') {
              return;
            }

            drawing.current = false;

            pubSub.publish('UPDATE_HOTSPOT_POINTS', {
              points: rectState.current,
              updateRectangleHotspotLocalState: false,
            });
          }}
          onClick={e => {
            if (hotspotType === 'rectangle') {
              return;
            }

            e.persist();

            pubSub.publish('UPDATE_HOTSPOT_POINTS', {
              points: [
                (e.clientX - root.current!.getBoundingClientRect().left) *
                  inverseScale,
                (e.clientY - root.current!.getBoundingClientRect().top) *
                  inverseScale,
              ],
            });
          }}
        >
          {[0, 2].includes(imgLoading) ? (
            <svg
              viewBox={`0 0 ${imgSize[0]} ${imgSize[1]}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <image
                xlinkHref={image || hotspotDefaultSVG}
                width={imgSize[0]}
                height={imgSize[1]}
                style={{
                  pointerEvents: 'none',
                }}
              />

              {(!window.dev() ? true : selected) &&
                points
                  .filter(
                    (points: any) =>
                      hotspotType === 'rectangle' || points?.length
                  )
                  .map((points: any, i) => {
                    const id = children[i]?.props.id;

                    if (hotspotType === 'rectangle') {
                      return (
                        <Rectangle
                          key={`hotspot-${i}`}
                          id={`hotspot-${id}`}
                          name={names[i]}
                          root={root}
                          inverseScale={inverseScale}
                          color={getParsedColor(0.8)}
                          points={points}
                          currentHotspotId={currentHotspotId}
                          onClick={e => {
                            //
                            setShowFeedback(hotSpots ? names[i] : id);
                          }}
                        />
                      );
                    }

                    if (hotspotType === 'polygon') {
                      return (
                        <Polygon
                          key={`hotspot-${i}-${hotSpotColor}`}
                          id={`hotspot-${id}`}
                          points={points.join(' ')}
                          hotSpotsVisible={hotSpotsVisible}
                          r={r}
                          g={g}
                          b={b}
                          hotSpotColor={hotSpotColor}
                          onClick={(e: any) => {
                            // e.stopPropagation();
                            //
                            setShowFeedback(hotSpots ? names[i] : id);

                            requestAnimationFrame(() => {
                              hasBeenSeen[id] = true;
                            });
                          }}
                          hasBeenSeen={hasBeenSeen[id]}
                          showSeenState={showSeenState}
                          inverseScale={inverseScale}
                        />
                      );
                    }

                    if (hotspotType === 'circle') {
                      return (
                        <VisibleHotSpot
                          key={`hotspot-${i}`}
                          id={`hotspot-${id}`}
                          location={[points[0], points[1]]}
                          color={getParsedColor(0.8)}
                          hotSpotsAnimated={hotSpotsAnimated}
                          hotSpotsVisible={hotSpotsVisible}
                          size={hotSpotSize * inverseScale}
                          inverseScale={inverseScale}
                          creating={creating}
                          hasBeenSeen={hasBeenSeen[id]}
                          showSeenState={showSeenState}
                          onClick={(e: any) => {
                            // e.stopPropagation();
                            //
                            setShowFeedback(hotSpots ? names[i] : id);

                            requestAnimationFrame(() => {
                              hasBeenSeen[id] = true;
                            });
                          }}
                        />
                      );
                    }
                  })}
            </svg>
          ) : (
            <LoadingSpinner />
          )}
        </div>
      </div>
      {showFeedback && (
        <Feedback
          useMediaInFeedback={useMediaInFeedback}
          onClose={() => {
            setShowFeedback(null);
          }}
        >
          {feedback}
        </Feedback>
      )}
      {window.dev() && (!image || imgLoading === 2) && (
        <EditWrapper
          id={props.id}
          width={imgSize[0] * scale}
          height={imgSize[1] * scale}
        />
      )}
    </div>
  ) : null;
};

export default HotSpot;
